import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Mic, Camera as CameraIcon, Send, Loader2, Sparkles, Link2, TrendingUp, ChevronRight, X, AlertCircle, Brain, MessageCircle, Target, Square, Zap, Telescope, Heart } from 'lucide-react';
import { JournalEntry, Screen, LifeContext, Pattern, UserProgress, AITier, AIPersonalityKey } from '../types';
import { getPulseInsight, generateProactiveQuestion, calculateContextQuality, transcribeWithWhisper, EnhancedInsight } from '../services/openaiService';
import { UI_STRINGS } from '../constants/translations';
import { trackPageView, trackJournalCreated, trackVoiceStarted, trackVoiceCompleted } from '../src/lib/analytics';
import { useFeatureDiscovery } from '../contexts/FeatureDiscoveryContext';
import FeatureDiscovery from '../components/FeatureDiscovery';
// import ProgressDashboard from '../components/ProgressDashboard'; // Temporarily disabled
import { useGamification } from '../hooks/useGamification';
import ProactivePromptCard from '../components/ProactivePromptCard';
import TypewriterMemoryMode from '../components/TypewriterMemoryMode';
import SwipeableInsightCard from '../components/SwipeableInsightCard';
import { PersonalityHeaderButton, PersonalitySelectorModal } from '../components/PersonalitySelector';
import { DEFAULT_PERSONALITY_KEY } from '../shared/aiPersonalities';

interface HomeViewProps {
  context: LifeContext;
  entries: JournalEntry[];
  patterns?: Pattern[];
  decisions?: any[];
  userProgress?: UserProgress | null;
  isLoading?: boolean;
  entriesSynced?: boolean;
  onVoiceTap: () => void;
  onNavigate: (screen: Screen) => void;
  onNewEntry: (text: string, imageUrl?: string, mood?: string) => Promise<any>;
  onUpdateContext?: (updates: Partial<LifeContext>) => void;
  onPersonalityChange: (key: AIPersonalityKey) => Promise<void>;
}

// Translations for typewriter invitation bubble
const TYPEWRITER_BUBBLE_TEXT: Record<string, string> = {
  Italian: 'Prova la modalità ricordo ✨',
  English: 'Try memory mode ✨',
  Spanish: 'Prueba el modo recuerdo ✨',
  German: 'Probiere den Erinnerungsmodus ✨',
  French: 'Essaie le mode souvenir ✨'
};

// Custom Typewriter Icon component - vintage style
const TypewriterIcon: React.FC<{ size?: number; className?: string }> = ({ size = 18, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className={className}
  >
    {/* Main body */}
    <rect x="2" y="10" width="20" height="10" rx="2" fill="currentColor" opacity="0.15" stroke="currentColor" strokeWidth="1.5" />
    
    {/* Paper roll / platen */}
    <path d="M4 10 C4 7 6 5 8 5 L16 5 C18 5 20 7 20 10" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <rect x="7" y="3" width="10" height="4" rx="0.5" fill="currentColor" opacity="0.1" stroke="currentColor" strokeWidth="1" />
    
    {/* Paper coming out */}
    <path d="M8 3 L8 1 L16 1 L16 3" stroke="currentColor" strokeWidth="1" fill="none" strokeLinecap="round" />
    
    {/* Keys - 3 rows */}
    <circle cx="6" cy="13" r="1.2" fill="currentColor" opacity="0.8" />
    <circle cx="9.5" cy="13" r="1.2" fill="currentColor" opacity="0.8" />
    <circle cx="13" cy="13" r="1.2" fill="currentColor" opacity="0.8" />
    <circle cx="16.5" cy="13" r="1.2" fill="currentColor" opacity="0.8" />
    
    {/* Space bar */}
    <rect x="6" y="16" width="12" height="2" rx="1" fill="currentColor" opacity="0.6" />
    
    {/* Carriage return lever */}
    <path d="M20 8 L22 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const HomeView: React.FC<HomeViewProps> = ({ context, entries, patterns = [], decisions = [], userProgress, isLoading = false, entriesSynced = true, onNavigate, onNewEntry, onUpdateContext, onPersonalityChange }) => {
  const t = useMemo(() => UI_STRINGS[context.language] || UI_STRINGS.English, [context.language]);
  const safeEntries = Array.isArray(entries) ? entries : [];
  const { state: discoveryState, getNextStepForView, completeStep } = useFeatureDiscovery();
  const { progress: gamificationProgress, aiTier } = useGamification();
  const [dismissedPromptId, setDismissedPromptId] = useState<string | null>(null);
  const [showTypewriterBubble, setShowTypewriterBubble] = useState(false);
  const [typewriterBubbleDismissed, setTypewriterBubbleDismissed] = useState(false);

  // AI Personality state
  const [isPersonalityModalOpen, setIsPersonalityModalOpen] = useState(false);
  const currentPersonalityKey = context.selectedPersonalityKey || DEFAULT_PERSONALITY_KEY;

  // Track if we've already generated content for this mount cycle (prevents duplicates)
  const hasGeneratedInsightRef = useRef(false);
  const hasGeneratedQuestionRef = useRef(false);

  // Check localStorage for typewriter mode usage (fallback when DB column doesn't exist)
  const [localTypewriterUsed, setLocalTypewriterUsed] = useState(() => {
    try {
      return localStorage.getItem('typewriter_mode_used') === 'true';
    } catch {
      return false;
    }
  });

  // Combined check: either from context (DB) or localStorage
  const hasUsedTypewriter = context.typewriterModeUsed || localTypewriterUsed;

  const currentDiscoveryStep = useMemo(() => getNextStepForView('home'), [getNextStepForView]);

  const currentStreak = gamificationProgress?.currentStreak || userProgress?.currentStreak || 0;


  useEffect(() => {
    trackPageView('/home', 'Home', true);
  }, []);

  // Show typewriter bubble invitation for users who haven't tried it
  useEffect(() => {
    if (!hasUsedTypewriter && !typewriterBubbleDismissed && safeEntries.length >= 1) {
      const timer = setTimeout(() => {
        setShowTypewriterBubble(true);
        // Auto-hide after 8 seconds
        setTimeout(() => setShowTypewriterBubble(false), 8000);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [hasUsedTypewriter, typewriterBubbleDismissed, safeEntries.length]);

  const [pulseInsight, setPulseInsight] = useState<EnhancedInsight | null>(null);
  const [isLoadingInsight, setIsLoadingInsight] = useState(true);
  const [inputText, setInputText] = useState("");
  const [pendingImage, setPendingImage] = useState<string | null>(null);
  const [isTypewriterOpen, setIsTypewriterOpen] = useState(false);

  // Feedback state for insights
  const [pulseInsightFeedback, setPulseInsightFeedback] = useState<'helpful' | 'not_helpful' | null>(null);
  const [questionFeedback, setQuestionFeedback] = useState<'helpful' | 'not_helpful' | null>(null);

  // Proactive AI state
  const [proactiveQuestion, setProactiveQuestion] = useState<{
    question: string;
    reason: string;
    relatedEntryId?: string;
    type: string;
  } | null>(null);
  const [isLoadingQuestion, setIsLoadingQuestion] = useState(false);

  // Submit insight feedback to API and update local state
  const submitInsightFeedback = useCallback(async (
    insightType: 'pulse' | 'proactive_question',
    insightContent: string,
    feedbackType: 'helpful' | 'not_helpful'
  ) => {
    try {
      await fetch('/api/insight-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          insightType,
          insightContent,
          feedbackType,
          contextSnapshot: {
            entriesCount: safeEntries.length,
            language: context.language,
            timestamp: Date.now()
          }
        })
      });

      // If feedback is negative, add this insight content to rejected list
      // so it won't be used for future AI generations
      if (feedbackType === 'not_helpful') {
        // Reload rejected content to get updated list
        const res = await fetch('/api/insight-feedback/rejected');
        if (res.ok) {
          const data = await res.json();
          setRejectedBlockers(data.rejectedBlockers || []);
        }
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  }, [safeEntries.length, context.language]);

  // Voice recording state (MediaRecorder-based for mobile compatibility)
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [voiceError, setVoiceError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const discoveryFallbackTimerRef = useRef<NodeJS.Timeout | null>(null);
  const discoveryStateRef = useRef(discoveryState);
  const completeStepRef = useRef(completeStep);

  // Voice recording refs (MediaRecorder-based)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Keep refs updated for use in timeouts
  useEffect(() => {
    discoveryStateRef.current = discoveryState;
    completeStepRef.current = completeStep;
  }, [discoveryState, completeStep]);

  // Cleanup voice recording and discovery timer on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      if (discoveryFallbackTimerRef.current) {
        clearTimeout(discoveryFallbackTimerRef.current);
      }
    };
  }, []);

  // Calculate context quality score
  const contextQuality = useMemo(() =>
    calculateContextQuality(context, safeEntries, patterns, decisions),
    [context, safeEntries.length, patterns.length, decisions.length]
  );

  // State for rejected content (blockers/insights that user marked as not helpful)
  const [rejectedBlockers, setRejectedBlockers] = useState<string[]>([]);
  const [feedbackContext, setFeedbackContext] = useState<{
    rejectedInsights: string[];
    rejectedBlockers: string[];
    rejectedPatterns: string[];
    helpfulPatterns: string[];
    feedbackStats: { totalHelpful: number; totalNotHelpful: number; preferredInsightTypes: string[] };
  } | null>(null);

  // Load full feedback context on mount (for AI improvement)
  useEffect(() => {
    const loadFeedbackContext = async () => {
      try {
        const res = await fetch('/api/insight-feedback/ai-context');
        if (res.ok) {
          const data = await res.json();
          setFeedbackContext(data);
          setRejectedBlockers(data.rejectedBlockers || []);
        }
      } catch (e) {
        console.error("Failed to load feedback context:", e);
        // Fallback to just rejected content
        try {
          const res = await fetch('/api/insight-feedback/rejected');
          if (res.ok) {
            const data = await res.json();
            setRejectedBlockers(data.rejectedBlockers || []);
          }
        } catch {}
      }
    };
    loadFeedbackContext();
  }, []);

  // Create filtered context that excludes rejected blockers
  const filteredContext = useMemo(() => {
    if (rejectedBlockers.length === 0) return context;

    const filtered = { ...context };
    if (filtered.blockers) {
      filtered.blockers = filtered.blockers.filter(
        (blocker: string) => !rejectedBlockers.some(
          rejected => blocker.toLowerCase().includes(rejected.toLowerCase()) ||
                      rejected.toLowerCase().includes(blocker.toLowerCase())
        )
      );
    }
    return filtered;
  }, [context, rejectedBlockers]);

  // Reset feedback state when insight changes
  useEffect(() => {
    setPulseInsightFeedback(null);
  }, [pulseInsight?.text]);

  useEffect(() => {
    setQuestionFeedback(null);
  }, [proactiveQuestion?.question]);

  // Load pulse insight - generate once per mount (new on each navigation/refresh)
  useEffect(() => {
    // Skip if we already generated for this mount cycle
    if (hasGeneratedInsightRef.current) {
      return;
    }

    // Need at least some entries to generate meaningful insight
    if (safeEntries.length === 0) {
      setPulseInsight({ text: t.home.readyToListen, insightType: 'gentle_support', emotionalContext: 'fallback' });
      setIsLoadingInsight(false);
      return;
    }

    // Mark as generating to prevent duplicate calls
    hasGeneratedInsightRef.current = true;

    const load = async () => {
      setIsLoadingInsight(true);
      try {
        // Use filtered context (excludes rejected blockers) + feedback for AI improvement
        const res = await getPulseInsight(filteredContext, safeEntries, patterns, decisions, feedbackContext || undefined);
        setPulseInsight(res);
      } catch (e) {
        setPulseInsight({ text: t.home.readyToListen, insightType: 'gentle_support', emotionalContext: 'fallback' });
      } finally {
        setIsLoadingInsight(false);
      }
    };
    load();
  }, []); // Empty deps - run only on mount, ref prevents duplicates

  // Load proactive question - generate once per mount (new on each navigation/refresh)
  useEffect(() => {
    if (safeEntries.length < 2) return; // Need at least some entries

    // Skip if we already generated for this mount cycle
    if (hasGeneratedQuestionRef.current) {
      return;
    }

    // Mark as generating to prevent duplicate calls
    hasGeneratedQuestionRef.current = true;

    const loadQuestion = async () => {
      setIsLoadingQuestion(true);
      try {
        // Use filtered context (excludes rejected blockers) + feedback for AI improvement
        const question = await generateProactiveQuestion(filteredContext, safeEntries, patterns, feedbackContext || undefined);
        setProactiveQuestion(question);
      } catch (e) {
        console.error("Failed to load proactive question:", e);
      } finally {
        setIsLoadingQuestion(false);
      }
    };
    loadQuestion();
  }, []); // Empty deps - run only on mount, ref prevents duplicates

  // Get language code for Whisper
  const getWhisperLanguage = () => {
    const langMap: Record<string, string> = {
      'Italian': 'it',
      'English': 'en',
      'Spanish': 'es',
      'German': 'de',
      'French': 'fr'
    };
    return langMap[context.language] || 'it';
  };

  // Start voice recording with MediaRecorder (works on all mobile browsers)
  const startRecording = useCallback(async () => {
    try {
      setVoiceError(null);
      setRecordingDuration(0);
      audioChunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' 
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(250);
      setIsRecording(true);
      trackVoiceStarted();

      durationIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);

    } catch (err: any) {
      console.error('Recording error:', err);
      if (err.name === 'NotAllowedError') {
        setVoiceError(t.home.micPermissionError);
      } else if (err.name === 'NotFoundError') {
        setVoiceError(t.home.noMicFound);
      } else {
        setVoiceError(t.home.recordingError);
      }
    }
  }, []);

  // Stop recording and transcribe
  const stopRecordingAndSave = useCallback(async () => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
      setIsRecording(false);
      return;
    }

    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }

    return new Promise<void>((resolve) => {
      const recorder = mediaRecorderRef.current!;
      
      recorder.onstop = async () => {
        setIsRecording(false);
        setIsTranscribing(true);

        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }

        try {
          const audioBlob = new Blob(audioChunksRef.current, { type: recorder.mimeType });
          
          if (audioBlob.size < 1000) {
            setVoiceError(t.home.recordingTooShort);
            setIsTranscribing(false);
            resolve();
            return;
          }

          const transcribedText = await transcribeWithWhisper(audioBlob, getWhisperLanguage());
          
          if (transcribedText && transcribedText.trim()) {
            trackVoiceCompleted(recordingDuration);
            trackJournalCreated(false);
            if (!discoveryState.homeInputDiscovered) {
              completeStep('homeInputDiscovered');
            }
            if (!discoveryState.voiceInputDiscovered) {
              completeStep('voiceInputDiscovered');
            }
            // Fire-and-forget: non bloccare l'utente durante l'upload
            onNewEntry(transcribedText.trim()).catch(err => {
              console.error('Error saving voice entry:', err);
            });
          } else {
            setVoiceError(t.home.noTextDetected);
          }
        } catch (err) {
          console.error('Transcription error:', err);
          setVoiceError(t.home.transcriptionError);
        } finally {
          setIsTranscribing(false);
          setRecordingDuration(0);
          audioChunksRef.current = [];
        }
        resolve();
      };

      recorder.stop();
    });
  }, [onNewEntry, context.language, discoveryState, completeStep]);

  // Cancel recording without saving
  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    audioChunksRef.current = [];
    setIsRecording(false);
    setRecordingDuration(0);
    setVoiceError(null);
  }, []);

  // Format recording duration
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Multilingual analyzing state messages
  const analyzingTexts: Record<string, { moment: string; photo: string; note: string }> = {
    Italian: { moment: 'Sto analizzando il momento...', photo: 'Sto analizzando la foto...', note: 'Sto analizzando la nota...' },
    English: { moment: 'Analyzing moment...', photo: 'Analyzing photo...', note: 'Analyzing note...' },
    Spanish: { moment: 'Analizando momento...', photo: 'Analizando foto...', note: 'Analizando nota...' },
    German: { moment: 'Moment wird analysiert...', photo: 'Foto wird analysiert...', note: 'Notiz wird analysiert...' },
    French: { moment: 'Analyse du moment...', photo: 'Analyse de la photo...', note: 'Analyse de la note...' },
  };
  const analyzingT = analyzingTexts[context.language] || analyzingTexts.English;

  // Simple tap to toggle recording
  const handleMicTap = () => {
    if (isRecording || isTranscribing) {
      return;
    }
    startRecording();
  };

  const handleSave = () => {
    if (!inputText.trim() && !pendingImage) return;

    // Fire-and-forget: salva immediatamente i valori e pulisci l'UI
    const textToSave = inputText.trim();
    const imageToSave = pendingImage;
    
    // Pulisci subito l'input per UX fluida (non bloccante)
    setInputText("");
    setPendingImage(null);
    
    if (!discoveryState.homeInputDiscovered) {
      completeStep('homeInputDiscovered');
    }
    
    // L'upload avviene in background - l'utente non aspetta
    onNewEntry(textToSave, imageToSave || undefined).catch(err => {
      console.error("Error during integration:", err);
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  // Compress image to reduce size for upload (max 1200px, 80% quality)
  const compressImage = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          // Calculate new dimensions (max 1200px on longest side)
          const maxSize = 1200;
          let { width, height } = img;

          if (width > height && width > maxSize) {
            height = (height / width) * maxSize;
            width = maxSize;
          } else if (height > maxSize) {
            width = (width / height) * maxSize;
            height = maxSize;
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to JPEG with 80% quality
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.8);
          resolve(compressedBase64);
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        // Compress image before storing
        const compressedImage = await compressImage(file);
        console.log(`📸 Compressed ${file.name}: ${(file.size / 1024).toFixed(0)}KB -> ${(compressedImage.length / 1024).toFixed(0)}KB base64`);
        setPendingImage(compressedImage);
      } catch (err) {
        console.error('Failed to compress image:', err);
        // Fallback to original if compression fails
        const reader = new FileReader();
        reader.onloadend = () => {
          setPendingImage(reader.result as string);
        };
        reader.readAsDataURL(file);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (!discoveryState.photoInputDiscovered) {
        setTimeout(() => completeStep('photoInputDiscovered'), 500);
      }
    }
  };

  // Typewriter mode save handler
  const handleTypewriterSave = useCallback((text: string) => {
    if (!text.trim()) return;

    if (!discoveryState.homeInputDiscovered) {
      completeStep('homeInputDiscovered');
    }

    trackJournalCreated(false);
    onNewEntry(text.trim()).catch(err => {
      console.error("Error saving typewriter entry:", err);
    });
  }, [onNewEntry, discoveryState, completeStep]);

  // Mark typewriter as used when user actually types (called from TypewriterMemoryMode)
  const handleTypewriterFirstType = useCallback(() => {
    // Hide the bubble only when user actually types
    setShowTypewriterBubble(false);
    setTypewriterBubbleDismissed(true);
    
    if (!hasUsedTypewriter) {
      // Save to localStorage (immediate, always works)
      try {
        localStorage.setItem('typewriter_mode_used', 'true');
        setLocalTypewriterUsed(true);
      } catch (e) {
        console.warn('Failed to save typewriter_mode_used to localStorage:', e);
      }

      // Also try to save to DB context
      if (onUpdateContext) {
        onUpdateContext({ typewriterModeUsed: true });
      }
    }
  }, [hasUsedTypewriter, onUpdateContext]);

  // Handle typewriter button click
  const handleTypewriterOpen = useCallback(() => {
    setIsTypewriterOpen(true);
  }, []);

  const handleDiscoveryActivate = useCallback((step: keyof typeof discoveryState) => {
    // No auto-advance: user must perform the action to complete the step
    if (step === 'homeInputDiscovered') {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else if (step === 'voiceInputDiscovered') {
      setTimeout(() => startRecording(), 300);
    } else if (step === 'photoInputDiscovered') {
      setTimeout(() => fileInputRef.current?.click(), 300);
    } else if (step === 'pulseIntro' || step === 'evolutionExplained') {
      // Non-action steps complete immediately
      completeStep(step);
    }
  }, [startRecording, completeStep]);

  const avgFlow = useMemo(() => {
    if (safeEntries.length === 0) return 0;
    const sum = safeEntries.reduce((acc, e) => acc + (e.goalAlignment || 50), 0);
    return Math.round(sum / safeEntries.length);
  }, [safeEntries]);

  const displayFirstName = context.userName ? context.userName.split(' ')[0] : t.home.defaultUser;

  // Get recent entries (last 10, sorted from newest to oldest)
  const recentEntries = useMemo(() => 
    [...safeEntries].sort((a, b) => b.timestamp - a.timestamp).slice(0, 10), 
    [safeEntries]
  );

  // Get active pattern - rotate on each navigation/refresh
  const [activePattern, setActivePattern] = useState<Pattern | null>(null);
  const hasSetPatternRef = useRef(false);

  useEffect(() => {
    if (patterns.length === 0) {
      setActivePattern(null);
      return;
    }

    // Skip if we already set a pattern for this mount cycle
    if (hasSetPatternRef.current) {
      return;
    }
    hasSetPatternRef.current = true;

    // Sort patterns by strength for consistent ordering
    const sortedPatterns = [...patterns].sort((a, b) => b.strength - a.strength);

    // Get last shown pattern index from localStorage
    let lastIndex = -1;
    try {
      const saved = localStorage.getItem('last_shown_pattern_index');
      if (saved !== null) {
        lastIndex = parseInt(saved, 10);
        // Reset if index is out of bounds (patterns changed)
        if (lastIndex >= sortedPatterns.length) {
          lastIndex = -1;
        }
      }
    } catch {}

    // Calculate next index (cycle through all patterns)
    const nextIndex = (lastIndex + 1) % sortedPatterns.length;

    // Save for next time
    try {
      localStorage.setItem('last_shown_pattern_index', nextIndex.toString());
    } catch {}

    setActivePattern(sortedPatterns[nextIndex]);
  }, []); // Empty deps - run only on mount, ref prevents duplicates

  // Format relative time
  const formatRelativeTime = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t.home.timeNow;
    if (minutes < 60) return `${minutes}${t.home.timeMinutesAgo}`;
    if (hours < 24) return `${hours}${t.home.timeHoursAgo}`;
    if (days === 1) return t.home.timeYesterday;
    return `${days}${t.home.timeDaysAgo}`;
  };

  // Get category color (supports all 5 languages)
  const getCategoryColor = (category: string) => {
    const normalizedCategory = category?.toLowerCase() || '';
    
    // Health category variants
    if (['salute', 'health', 'santé', 'gesundheit', 'salud'].includes(normalizedCategory)) {
      return 'bg-green-100 text-green-700';
    }
    // Focus category variants  
    if (['focus', 'concentration', 'konzentration', 'enfoque'].includes(normalizedCategory)) {
      return 'bg-blue-100 text-blue-700';
    }
    // Connection category variants
    if (['connessione', 'connection', 'connexion', 'verbindung', 'conexión'].includes(normalizedCategory)) {
      return 'bg-pink-100 text-pink-700';
    }
    // Spirit category variants
    if (['spirito', 'spirit', 'esprit', 'geist', 'espíritu'].includes(normalizedCategory)) {
      return 'bg-purple-100 text-purple-700';
    }
    // Finance category variants
    if (['finanza', 'finance', 'finances', 'finanzen', 'finanzas'].includes(normalizedCategory)) {
      return 'bg-amber-100 text-amber-700';
    }
    
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="h-full flex flex-col bg-white relative lg:bg-[#FAFAFA]">
      {/* Fixed Header */}
      <header className="shrink-0 px-5 lg:px-8 xl:px-12 pt-6 lg:pt-8 pb-4 lg:pb-6 bg-white/95 backdrop-blur-xl border-b border-black/[0.04] z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center">
            <div className="flex flex-col">
              <span className="text-[11px] lg:text-xs font-bold text-black/30 uppercase tracking-[0.2em]">
                {new Date().toLocaleDateString(
                  context.language === 'Italian' ? 'it-IT' : 
                  context.language === 'French' ? 'fr-FR' :
                  context.language === 'Spanish' ? 'es-ES' :
                  context.language === 'German' ? 'de-DE' : 'en-US', 
                  { weekday: 'long', day: 'numeric', month: 'short' }
                )}
              </span>
              <h1 className="text-[28px] lg:text-4xl xl:text-5xl font-black tracking-tight text-[#1D1D1F] leading-none mt-1 lg:mt-2">
                {t.home.greeting}, {displayFirstName}
              </h1>
            </div>
            
            {/* Streak, Personality & AI Tier Badge */}
            <div className="flex items-center gap-2">
              {currentStreak > 0 && (
                <div className="flex items-center gap-1.5 bg-gradient-to-r from-orange-100 to-amber-100 px-2.5 py-1.5 rounded-xl border border-orange-200/50">
                  <Zap size={14} className="text-orange-500" />
                  <span className="text-sm font-black text-orange-600">{currentStreak}</span>
                </div>
              )}

              {/* AI Personality Button */}
              <PersonalityHeaderButton
                personalityKey={currentPersonalityKey}
                language={context.language}
                onClick={() => setIsPersonalityModalOpen(true)}
                compact={true}
              />

              {/* AI Tier Badge - temporarily disabled
              <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-white shadow-sm ${
                aiTier === 'oracle' ? 'bg-gradient-to-r from-amber-400 to-orange-500' :
                aiTier === 'companion' ? 'bg-gradient-to-r from-purple-500 to-pink-600' :
                aiTier === 'learner' ? 'bg-gradient-to-r from-blue-500 to-indigo-600' :
                'bg-gradient-to-r from-gray-500 to-slate-600'
              }`}>
                {aiTier === 'oracle' ? <Sparkles size={12} /> :
                 aiTier === 'companion' ? <Heart size={12} /> :
                 aiTier === 'learner' ? <Brain size={12} /> :
                 <Telescope size={12} />}
                <span className="text-[10px] font-bold uppercase tracking-wide">
                  {aiTier === 'oracle' ? (context.language === 'Italian' ? 'Oracolo' : context.language === 'Spanish' ? 'Oráculo' : context.language === 'German' ? 'Orakel' : context.language === 'French' ? 'Oracle' : 'Oracle') :
                   aiTier === 'companion' ? (context.language === 'Italian' ? 'Compagno' : context.language === 'Spanish' ? 'Compañero' : context.language === 'German' ? 'Begleiter' : context.language === 'French' ? 'Compagnon' : 'Companion') :
                   aiTier === 'learner' ? (context.language === 'Italian' ? 'Studente' : context.language === 'Spanish' ? 'Aprendiz' : context.language === 'German' ? 'Lerner' : context.language === 'French' ? 'Apprenant' : 'Learner') :
                   (context.language === 'Italian' ? 'Esploratore' : context.language === 'Spanish' ? 'Explorador' : context.language === 'German' ? 'Entdecker' : context.language === 'French' ? 'Explorateur' : 'Explorer')}
                </span>
              </div>
              */}
            </div>
{/* Widget header nascosto temporaneamente
            <button
              onClick={() => onNavigate('insights')}
              className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl border border-black/[0.06] bg-white flex items-center justify-center shadow-sm active:scale-95 hover:shadow-md hover:border-indigo-200 transition-all"
            >
              <div className="relative w-9 h-9 lg:w-11 lg:h-11">
                <svg className="w-full h-full -rotate-90">
                  <circle cx="50%" cy="50%" r="45%" fill="transparent" stroke="rgba(0,0,0,0.04)" strokeWidth="3"/>
                  <circle cx="50%" cy="50%" r="45%" fill="transparent" stroke="#6366F1" strokeWidth="3" strokeDasharray={2 * Math.PI * 15} strokeDashoffset={2 * Math.PI * 15 * (1 - avgFlow / 100)} strokeLinecap="round" className="transition-all duration-1000"/>
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-[9px] lg:text-[11px] font-black">{avgFlow}%</span>
              </div>
            </button>
            */}
          </div>
        </div>
      </header>

      {/* Scrollable Content */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto overscroll-contain pb-40 lg:pb-48">
        <div className="max-w-4xl mx-auto">
          {/* Context Quality Badge - TEMPORARILY DISABLED
          {contextQuality.score > 0 && (
            <div className="px-5 lg:px-8 xl:px-12 pt-4 lg:pt-6">
              <button
                onClick={() => onNavigate('insights')}
                className="inline-flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-1.5 lg:py-2 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-full border border-indigo-100/50 active:scale-95 hover:shadow-md transition-all"
              >
                <Brain size={14} className="text-indigo-500 lg:w-4 lg:h-4" />
                <span className="text-[11px] lg:text-sm font-bold text-indigo-600">
                  {t.home.iKnowYouAt} {contextQuality.score}%
                </span>
                <div className="flex gap-0.5 lg:gap-1">
                  {[1, 2, 3, 4, 5].map(i => (
                    <div
                      key={i}
                      className={`w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full ${
                        i <= Math.ceil(contextQuality.score / 20) ? 'bg-indigo-500' : 'bg-indigo-200'
                      }`}
                    />
                  ))}
                </div>
              </button>
            </div>
          )}
          */}

          {/* Proactive AI Prompt Card - TEMPORARILY DISABLED
          {contextualAdvice && dismissedPromptId !== 'contextual-advice' && (
            <div className="px-5 lg:px-8 xl:px-12 pt-4 lg:pt-6">
              <ProactivePromptCard
                prompt={{
                  id: 'contextual-advice',
                  type: contextualAdvice.tone === 'celebratory' ? 'celebration' :
                        contextualAdvice.tone === 'reflective' ? 'reflection_prompt' :
                        contextualAdvice.tone === 'supportive' ? 'alignment_check' :
                        contextualAdvice.tone === 'encouraging' ? 'morning_intention' : 'morning_intention',
                  title: contextualAdvice.greeting,
                  content: contextualAdvice.insight + (contextualAdvice.suggestion ? ` ${contextualAdvice.suggestion}` : ''),
                  priority: 'medium',
                  generatedAt: Date.now(),
                  expiresAt: Date.now() + 86400000,
                  isViewed: false,
                  isDismissed: false,
                  suggestedAction: contextualAdvice.suggestion ? (
                    context.language === 'Italian' ? 'Rifletti' :
                    context.language === 'Spanish' ? 'Reflexiona' :
                    context.language === 'German' ? 'Reflektieren' :
                    context.language === 'French' ? 'Réfléchir' : 'Reflect'
                  ) : undefined
                }}
                language={context.language}
                onDismiss={() => setDismissedPromptId('contextual-advice')}
                onAction={() => onNavigate('consult')}
              />
            </div>
          )}
          */}

          {/* AI Pulse Insight - Apple-style swipeable */}
          <div className="px-5 lg:px-8 xl:px-12 py-5 lg:py-6">
            <div className="lg:bg-white lg:rounded-3xl lg:p-6 lg:shadow-sm lg:border lg:border-black/[0.04]">
              <SwipeableInsightCard
                content={pulseInsight?.text || t.home.readyToListen}
                isLoading={isLoadingInsight}
                language={context.language}
                onFeedback={(feedbackType) => {
                  setPulseInsightFeedback(feedbackType);
                  if (pulseInsight?.text) {
                    submitInsightFeedback('pulse', pulseInsight.text, feedbackType);
                  }
                }}
              >
                {/* Memory link for "remember when" insights */}
                {pulseInsight?.memoryLink && (
                  <button
                    onClick={() => onNavigate('archive')}
                    className="mt-2 flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700 transition-colors"
                  >
                    <Link2 size={12} />
                    <span className="opacity-75">{pulseInsight.memoryLink.date}</span>
                  </button>
                )}
              </SwipeableInsightCard>
            </div>
          </div>

          {/* Desktop: Two Column Layout for Cards */}
          <div className="lg:grid lg:grid-cols-2 lg:gap-4 lg:px-8 xl:px-12">
            {/* Proactive AI Question - Opens Chat with Agent-First Pattern */}
            {proactiveQuestion && !isLoadingQuestion && (
              <div className="px-5 lg:px-0 pb-4">
                <button
                  onClick={() => {
                    sessionStorage.setItem('chatContext', JSON.stringify({
                      type: 'pulse_insight',
                      data: { insightText: proactiveQuestion.question }
                    }));
                    onNavigate('consult');
                  }}
                  className="w-full p-4 lg:p-5 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200/50 rounded-2xl lg:rounded-3xl active:scale-[0.98] hover:shadow-lg hover:border-emerald-300 transition-all text-left"
                >
                  <div className="flex items-start gap-3 lg:gap-4">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                      <MessageCircle size={20} className="text-emerald-600 lg:w-6 lg:h-6" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[15px] lg:text-base font-bold text-emerald-800 leading-snug">
                        {proactiveQuestion.question}
                      </p>
                      <p className="text-[12px] lg:text-sm text-emerald-600/70 mt-1 lg:mt-2 leading-snug">
                        {proactiveQuestion.reason}
                      </p>
                    </div>
                    <ChevronRight size={18} className="text-emerald-400 shrink-0 mt-1 lg:w-5 lg:h-5" />
                  </div>
                </button>
              </div>
            )}

            {/* Pattern Alert (if active) */}
            {activePattern && (
              <div className="px-5 lg:px-0 pb-4">
                <button
                  onClick={() => {
                    // Set scroll target before navigating
                    localStorage.setItem('insights_scroll_target', 'patterns-section');
                    onNavigate('insights');
                  }}
                  className="w-full p-4 lg:p-5 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/50 rounded-2xl lg:rounded-3xl flex items-center gap-3 lg:gap-4 active:scale-[0.98] hover:shadow-lg hover:border-amber-300 transition-all"
                >
                  <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl bg-amber-500/10 flex items-center justify-center">
                    <TrendingUp size={20} className="text-amber-600 lg:w-6 lg:h-6" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-[13px] lg:text-base font-bold text-amber-800">{activePattern.title}</p>
                    <p className="text-[12px] lg:text-sm text-amber-600/80 leading-relaxed">{activePattern.description}</p>
                  </div>
                  <ChevronRight size={18} className="text-amber-400 lg:w-5 lg:h-5" />
                </button>
              </div>
            )}
          </div>

          {/* Recent Entries */}
          <div className="px-5 lg:px-8 xl:px-12 pb-6">
            <div className="flex items-center justify-between mb-3 lg:mb-4">
              <h2 className="text-[13px] lg:text-sm font-bold text-black/40 uppercase tracking-wider">{t.home.recent}</h2>
              <button
                onClick={() => onNavigate('archive')}
                className="text-[13px] lg:text-sm font-bold text-indigo-500 active:opacity-70 hover:text-indigo-700 transition-colors"
              >
                {t.home.viewAll}
              </button>
            </div>

            {recentEntries.length === 0 ? (
              <div className="py-12 lg:py-20 text-center lg:bg-white lg:rounded-3xl lg:border lg:border-black/[0.04]">
                {isLoading || !entriesSynced ? (
                  <>
                    <div className="w-16 h-16 lg:w-20 lg:h-20 mx-auto mb-4 lg:mb-6 rounded-3xl bg-indigo-50 flex items-center justify-center">
                      <Loader2 size={28} className="text-indigo-400 lg:w-9 lg:h-9 animate-spin" />
                    </div>
                    <p className="text-[15px] lg:text-lg font-medium text-black/40">
                      {context.language === 'Italian' ? 'Caricamento momenti...' :
                       context.language === 'Spanish' ? 'Cargando momentos...' :
                       context.language === 'German' ? 'Momente werden geladen...' :
                       context.language === 'French' ? 'Chargement des moments...' :
                       'Loading moments...'}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 lg:w-20 lg:h-20 mx-auto mb-4 lg:mb-6 rounded-3xl bg-gray-50 flex items-center justify-center">
                      <Sparkles size={28} className="text-gray-300 lg:w-9 lg:h-9" />
                    </div>
                    <p className="text-[15px] lg:text-lg font-medium text-black/30">
                      {t.home.startCapturing}
                    </p>
                    <p className="text-[13px] lg:text-base text-black/20 mt-1">
                      {t.home.writeOrHoldMic}
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-3 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0">
                {recentEntries.map((entry) => (
                  <div
                    key={entry.id}
                    className={`relative p-4 lg:p-5 rounded-2xl lg:rounded-3xl border transition-all hover:shadow-md overflow-hidden ${
                      entry.isAnalyzing
                        ? 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200'
                        : 'bg-white border-black/[0.04] shadow-sm hover:border-indigo-100'
                    }`}
                  >
                    {/* Analyzing Overlay */}
                    {entry.isAnalyzing && (
                      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/10 z-10 backdrop-blur-[1px] flex items-center justify-center">
                        <div className="flex flex-col items-center gap-2 px-4 py-3 bg-white/90 rounded-2xl shadow-lg border border-indigo-100">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md">
                            <div className="spinner spinner-white" style={{ width: '20px', height: '20px', borderWidth: '2px' }} />
                          </div>
                          <p className="text-[13px] font-bold text-indigo-700 text-center">
                            {entry.imageUrl ? analyzingT.photo : analyzingT.moment}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className={`flex items-start gap-3 lg:gap-4 ${entry.isAnalyzing ? 'opacity-40' : ''}`}>
                      {/* Emoji/Image */}
                      <div className="shrink-0">
                        {entry.imageUrl ? (
                          <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-xl lg:rounded-2xl overflow-hidden">
                            <img src={entry.imageUrl} className="w-full h-full object-cover" alt="" />
                          </div>
                        ) : (
                          <span className="text-2xl lg:text-3xl">{entry.emotion || '✨'}</span>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[11px] lg:text-xs font-semibold text-black/30">
                            {formatRelativeTime(entry.timestamp)}
                          </span>
                          {entry.category && !entry.isAnalyzing && (
                            <span className={`text-[10px] lg:text-xs font-bold px-2 py-0.5 rounded-full ${getCategoryColor(entry.category)}`}>
                              {(t.categories as Record<string, string>)?.[entry.category] || entry.category}
                            </span>
                          )}
                        </div>

                        <p className="text-[15px] lg:text-base font-medium text-[#1D1D1F] leading-snug">
                          {entry.content}
                        </p>

                        {/* AI Insight */}
                        {entry.aiInsight && !entry.isAnalyzing && (
                          <div className="mt-2 flex items-start gap-2">
                            <Sparkles size={12} className="text-indigo-400 shrink-0 mt-0.5" />
                            <p className="text-[13px] lg:text-sm text-indigo-600/80 leading-snug">
                              {entry.aiInsight}
                            </p>
                          </div>
                        )}

                        {/* Semantic Links - Expanded view */}
                        {entry.relatedTo && entry.relatedTo.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-black/[0.05]">
                            <div className="flex items-center gap-1.5 mb-2">
                              <Link2 size={11} className="text-indigo-500" />
                              <span className="text-[10px] font-bold uppercase tracking-wider text-black/30">
                                {entry.relatedTo.length} {entry.relatedTo.length === 1 ? t.home.connection : t.home.connections}
                              </span>
                            </div>
                            <div className="space-y-1.5">
                              {entry.relatedTo.slice(0, 2).map((link, linkIdx) => {
                                const linkedEntry = safeEntries.find(e => e.id === link.targetEntryId);
                                if (!linkedEntry) return null;
                                return (
                                  <div key={linkIdx} className="flex items-start gap-2 p-2 bg-indigo-50/50 rounded-xl border border-indigo-100/50">
                                    <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] shrink-0">
                                      {linkedEntry.emotion}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[11px] font-medium text-black/70 leading-snug line-clamp-1">
                                        {linkedEntry.content}
                                      </p>
                                      <span className="text-[9px] font-bold text-indigo-500/70">{link.strength}%</span>
                                    </div>
                                  </div>
                                );
                              })}
                              {entry.relatedTo.length > 2 && (
                                <span className="text-[10px] text-black/30 font-medium">
                                  +{entry.relatedTo.length - 2}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bottom spacer for input bar */}
          <div className="h-32 lg:h-40" />
        </div>
      </div>

      {/* Voice Recording Overlay */}
      {(isRecording || isTranscribing) && (
        <div className="fixed inset-0 bg-black/95 z-40 flex flex-col items-center justify-center">
          <div className="text-center px-8 w-full max-w-sm">
            {isTranscribing ? (
              <>
                <div className="w-28 h-28 mx-auto mb-8 rounded-full bg-indigo-500 flex items-center justify-center shadow-2xl shadow-indigo-500/40">
                  <div className="spinner spinner-xl spinner-white" />
                </div>
                <p className="text-white text-[20px] font-semibold mb-2">{t.home.transcribing}</p>
                <p className="text-white/50 text-[15px]">{t.home.oneMoment}</p>
              </>
            ) : (
              <>
                <div className="w-32 h-32 mx-auto mb-8 rounded-full bg-red-500 flex items-center justify-center shadow-2xl shadow-red-500/50 animate-pulse">
                  <Mic size={48} className="text-white" />
                </div>
                
                <div className="mb-8">
                  <p className="text-white text-[48px] font-mono font-bold mb-2">
                    {formatDuration(recordingDuration)}
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <p className="text-white/60 text-[15px] font-medium">{t.home.recordingInProgress}</p>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-4">
                  <button
                    onClick={cancelRecording}
                    className="w-16 h-16 rounded-full bg-white/10 text-white flex items-center justify-center active:scale-95 transition-all"
                  >
                    <X size={28} />
                  </button>
                  <button
                    onClick={stopRecordingAndSave}
                    disabled={recordingDuration < 1}
                    className={`flex-1 h-16 rounded-full font-bold text-[17px] active:scale-95 transition-all flex items-center justify-center gap-3 ${
                      recordingDuration >= 1
                        ? 'bg-white text-black'
                        : 'bg-white/20 text-white/40'
                    }`}
                  >
                    <Square size={20} fill="currentColor" />
                    {t.home.save}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Apple-style Fixed Input Bar */}
      <div 
        className="fixed left-0 right-0 z-50 p-3 lg:p-4 lg:left-1/2 lg:-translate-x-1/2 lg:w-full lg:max-w-2xl xl:max-w-3xl" 
        style={{ bottom: 'calc(56px + env(safe-area-inset-bottom, 8px) + 8px)' }}
      >
        {/* Image Preview */}
        {pendingImage && (
          <div className="absolute bottom-full mb-3 left-3 lg:left-0 animate-in slide-in-from-bottom-4 duration-300">
            <div className="relative w-20 h-20 lg:w-24 lg:h-24 rounded-2xl lg:rounded-3xl overflow-hidden border-2 border-white shadow-2xl">
              <img src={pendingImage} className="w-full h-full object-cover" alt="Preview" />
              <button
                onClick={() => setPendingImage(null)}
                className="absolute top-1.5 right-1.5 lg:top-2 lg:right-2 w-5 h-5 lg:w-6 lg:h-6 bg-black/60 text-white rounded-full flex items-center justify-center backdrop-blur-md hover:bg-black/80 transition-colors"
              >
                <X size={10} className="lg:w-3 lg:h-3" />
              </button>
            </div>
          </div>
        )}

        {/* Voice Error */}
        {voiceError && (
          <div className="absolute bottom-full mb-3 left-3 lg:left-0 right-3 lg:right-0">
            <div className="flex items-center gap-2 text-red-500 text-[13px] font-medium bg-red-50 px-4 py-2 rounded-2xl">
              <AlertCircle size={14} />
              {voiceError}
            </div>
          </div>
        )}

        {/* Input Container */}
        <div className="bg-white/98 backdrop-blur-3xl rounded-2xl lg:rounded-[28px] xl:rounded-[32px] p-2 lg:p-3 border border-black/[0.06] shadow-[0_8px_40px_rgba(0,0,0,0.12)] lg:shadow-[0_16px_64px_rgba(0,0,0,0.14)] flex items-center gap-1.5 lg:gap-2 transition-all hover:shadow-[0_20px_60px_rgba(0,0,0,0.18)]">
          {/* Typewriter Mode Button with invitation bubble */}
          <div className="relative">
            <button
              onClick={handleTypewriterOpen}
              className="w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center transition-all rounded-xl lg:rounded-2xl hover:bg-amber-50 text-amber-600/70 hover:text-amber-700 active:scale-95"
              title="Typewriter Mode"
            >
              <TypewriterIcon size={20} className="lg:w-6 lg:h-6" />
            </button>

            {/* Invitation bubble for new users */}
            {showTypewriterBubble && !hasUsedTypewriter && (
              <div
                className="absolute bottom-full left-0 mb-2 whitespace-nowrap animate-in fade-in slide-in-from-bottom-2 duration-300"
                onClick={() => {
                  setShowTypewriterBubble(false);
                  setTypewriterBubbleDismissed(true);
                }}
              >
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[12px] font-semibold px-3 py-1.5 rounded-full shadow-lg shadow-amber-500/30">
                  {TYPEWRITER_BUBBLE_TEXT[context.language] || TYPEWRITER_BUBBLE_TEXT.English}
                </div>
                {/* Bubble arrow - positioned on the left side */}
                <div className="absolute top-full left-4 -mt-1">
                  <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-orange-500" />
                </div>
              </div>
            )}
          </div>

          {/* Camera Button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className={`w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center transition-all rounded-xl lg:rounded-2xl hover:bg-gray-100 ${pendingImage ? 'text-indigo-600' : 'text-black/25 hover:text-black/50'}`}
          >
            <CameraIcon size={20} className="lg:w-6 lg:h-6" />
          </button>

          {/* Text Input */}
          <textarea
            ref={inputRef}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t.home.placeholder}
            rows={1}
            className="flex-1 px-2 lg:px-4 py-2.5 lg:py-3.5 font-semibold outline-none text-[#1D1D1F] placeholder:text-black/20 text-[16px] lg:text-[17px] xl:text-[18px] bg-transparent resize-none max-h-32 leading-snug"
            style={{ minHeight: '40px' }}
          />

          {/* Send or Mic Button */}
          {inputText.trim() || pendingImage ? (
            <button
              onClick={handleSave}
              className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl bg-[#1D1D1F] flex items-center justify-center transition-all duration-300 shadow-lg shadow-black/20 hover:bg-black hover:scale-105 shrink-0"
            >
              <Send size={16} className="lg:w-5 lg:h-5 text-white" />
            </button>
          ) : (
            <button
              onClick={handleMicTap}
              disabled={isRecording}
              className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl bg-[#1D1D1F] flex items-center justify-center transition-all duration-300 shadow-lg shadow-black/20 hover:bg-black hover:scale-105 shrink-0"
            >
              <Mic size={18} className="lg:w-5 lg:h-5 text-white" />
            </button>
          )}
        </div>
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        onChange={handleFileChange}
      />

      {/* Feature Discovery Overlay */}
      {currentDiscoveryStep && (
        <FeatureDiscovery
          featureKey={currentDiscoveryStep}
          discoveryState={discoveryState}
          onDismiss={completeStep}
          onActivate={handleDiscoveryActivate}
          onNavigate={onNavigate}
          currentView="pulse"
          language={context.language}
          position="bottom"
        />
      )}

      {/* Typewriter Memory Mode */}
      <TypewriterMemoryMode
        isOpen={isTypewriterOpen}
        onClose={() => setIsTypewriterOpen(false)}
        onSave={handleTypewriterSave}
        onFirstType={handleTypewriterFirstType}
        language={context.language}
      />

      {/* AI Personality Selector Modal */}
      <PersonalitySelectorModal
        isOpen={isPersonalityModalOpen}
        onClose={() => setIsPersonalityModalOpen(false)}
        currentPersonalityKey={currentPersonalityKey}
        onSelect={onPersonalityChange}
        language={context.language}
      />
    </div>
  );
};

export default HomeView;
